import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, LinearProgress, Snackbar, Alert, Backdrop, Typography, Paper, CircularProgress, useTheme, alpha,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import S3ApiService from '../../../services/s3-api.service';
import CodeEditor from '../CodeEditor';

import useAssetBrowser from './useAssetBrowser';
import useSelection from './useSelection';
import Breadcrumbs from './Breadcrumbs';
import Toolbar from './Toolbar';
import ContextMenu from './ContextMenu';
import GridView from './GridView';
import ListView from './ListView';
import Lightbox from './Lightbox';
import { NewFolderDialog, RenameDialog, DeleteDialog, MoveDialog } from './dialogs';
import {
  ASSETS_ROOT, isEditableFile,
  logicalToRelative,
  sanitizeFileName,
} from './pathUtils';

/**
 * Programmatic browser download. Setting `download` is advisory — for
 * cross-origin URLs to actually save (instead of navigating), the server must
 * also send Content-Disposition: attachment. Our /s3/zip endpoint does that.
 */
function triggerBrowserDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Asset explorer. Public props:
 *
 *  organizationName, sceneName       — required (used to build S3 keys)
 *  captionPathPrefix                 — optional; "<captionId>/<version>" to browse a caption's assets
 *  path / onPathChange               — optional controlled path (logical, e.g. "assets/foo/bar").
 *                                      When provided the parent owns the URL state.
 *  initialPath                       — used only when `path` is not provided (uncontrolled mode).
 *  darkMode                          — colour hint (default true)
 *  hideBreadcrumbs                   — hide internal breadcrumbs when host supplies its own
 *  onFileSelect                      — callback when a file is single-clicked
 */
export default function AssetExplorer({
  organizationName = '',
  sceneName = '',
  captionPathPrefix = '',
  path: controlledPath,
  onPathChange,
  initialPath = ASSETS_ROOT,
  darkMode = true,
  hideBreadcrumbs = false,
  onFileSelect,
}) {
  const theme = useTheme();
  const rendererBaseUrl = (process.env.REACT_APP_SCENE_RENDERER_URL || '').replace(/\/$/, '');

  const [uncontrolledPath, setUncontrolledPath] = useState(initialPath || ASSETS_ROOT);
  const path = controlledPath ?? uncontrolledPath;
  const handlePathChange = useCallback((next) => {
    if (onPathChange) onPathChange(next);
    else setUncontrolledPath(next);
  }, [onPathChange]);

  const browser = useAssetBrowser({
    organizationName, sceneName, captionPathPrefix,
    path, onPathChange: handlePathChange, rendererBaseUrl,
  });

  const orderedItems = useMemo(
    () => [...browser.contents.folders, ...browser.contents.files],
    [browser.contents],
  );

  const selection = useSelection(orderedItems);

  // -- toolbar state --------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('assetExplorer.viewMode') || 'grid'; } catch (_) { return 'grid'; }
  });
  const [sort, setSort] = useState(() => {
    try { return localStorage.getItem('assetExplorer.sort') || 'name-asc'; } catch (_) { return 'name-asc'; }
  });
  useEffect(() => { try { localStorage.setItem('assetExplorer.viewMode', viewMode); } catch (_) {} }, [viewMode]);
  useEffect(() => { try { localStorage.setItem('assetExplorer.sort', sort); } catch (_) {} }, [sort]);

  // -- dialogs --------------------------------------------------------------
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTargets, setDeleteTargets] = useState(null);
  const [moveTargets, setMoveTargets] = useState(null);
  const [inlineRenameOf, setInlineRenameOf] = useState(null);

  // -- preview / lightbox ---------------------------------------------------
  const [lightboxStart, setLightboxStart] = useState(null);
  const [codeEditor, setCodeEditor] = useState(null);
  // -- transient ux ---------------------------------------------------------
  const [toast, setToast] = useState(null);
  const [isDraggingExternal, setIsDraggingExternal] = useState(false);
  const [dropTargetPath, setDropTargetPath] = useState(null);
  const [crumbDropTargetPath, setCrumbDropTargetPath] = useState(null);
  const [upload, setUpload] = useState({ active: false, done: 0, total: 0 });

  // -- filtering + sorting --------------------------------------------------
  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filterFn = (it) => !q || it.name.toLowerCase().includes(q);
    const folders = browser.contents.folders.filter(filterFn);
    const files = browser.contents.files.filter(filterFn);
    const cmp = sortFn(sort);
    return { folders: [...folders].sort(cmp), files: [...files].sort(cmp) };
  }, [browser.contents, searchQuery, sort]);

  const visibleOrdered = useMemo(
    () => [...visible.folders, ...visible.files],
    [visible],
  );

  // -- thumbnail URLs (image files only) -----------------------------------
  const imageUrls = useMemo(() => {
    const map = {};
    for (const f of browser.contents.files) {
      if (f.fileType === 'image') map[f.logicalPath] = browser.buildPublicUrl(f.fullPath || f.s3Key);
    }
    return map;
  }, [browser.contents.files, browser.buildPublicUrl]);

  const visibleImages = useMemo(
    () => browser.contents.files.filter((f) => f.fileType === 'image'),
    [browser.contents.files],
  );

  // -- handlers -------------------------------------------------------------
  const showError = useCallback((msg) => setToast({ severity: 'error', message: msg }), []);
  const showInfo = useCallback((msg) => setToast({ severity: 'info', message: msg }), []);
  const showSuccess = useCallback((msg) => setToast({ severity: 'success', message: msg }), []);

  // when the browsed folder changes, clear selection
  useEffect(() => { selection.clearSelection(); }, [browser.currentLogicalPath]); // eslint-disable-line

  const onItemClick = useCallback((item, e) => {
    selection.handleClick(item.logicalPath, e);
    if (item.type === 'file') onFileSelect?.(item);
  }, [selection, onFileSelect]);

  const onItemDoubleClick = useCallback((item) => {
    if (item.type === 'folder') {
      browser.navigateTo(item.logicalPath);
    } else if (item.fileType === 'image') {
      setLightboxStart(item.logicalPath);
    } else if (isEditableFile(item)) {
      setCodeEditor(item);
    } else {
      // direct download / open via public URL
      const url = browser.buildPublicUrl(item.fullPath || item.s3Key);
      window.open(url, '_blank', 'noopener');
    }
  }, [browser]);

  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, item: null });
  const openContextMenu = useCallback((e, item) => {
    e.preventDefault(); e.stopPropagation();
    // ensure clicked item is at least selected
    if (!selection.isSelected(item.logicalPath)) selection.selectOnly(item.logicalPath);
    setCtxMenu({ open: true, x: e.clientX, y: e.clientY, item });
  }, [selection]);
  const closeContextMenu = useCallback(() => setCtxMenu((c) => ({ ...c, open: false })), []);

  // -- download: hands off to /s3/zip endpoint, browser owns the save dialog --
  //
  // For a single file: the endpoint streams the file directly with
  // Content-Disposition: attachment, so the original filename is preserved.
  // For multiple items / folders: the endpoint streams a zip.
  // Either way we never hold bytes in browser memory, never re-encode, never
  // double-pay the bandwidth.
  const downloadAsZip = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    try {
      showInfo('Preparing download…');

      // Build the payload. Server expands folder prefixes recursively.
      const payload = {
        organizationName, sceneName,
      };
      if (items.length === 1) {
        const only = items[0];
        payload.sourceKey = only.type === 'folder' ? `${only.s3Key}/` : only.s3Key;
        payload.isFolder = only.type === 'folder';
        payload.filename = only.type === 'folder' ? `${only.name}.zip` : only.name;
      } else {
        payload.keys = items.map((i) => ({
          key: i.type === 'folder' ? `${i.s3Key}/` : i.s3Key,
          isFolder: i.type === 'folder',
        }));
        payload.filename = `assets-${new Date().toISOString().slice(0, 10)}.zip`;
      }

      const downloadId = await S3ApiService.prepareZipDownload(payload);
      const url = S3ApiService.buildZipDownloadUrl(downloadId);
      triggerBrowserDownload(url, payload.filename);
    } catch (err) {
      console.error('Download failed:', err);
      showError(err.message || 'Download failed');
    }
  }, [organizationName, sceneName, showError, showInfo]);

  // -- copy public link ----------------------------------------------------
  const copyLink = useCallback(async (item) => {
    const url = browser.buildPublicUrl(item.fullPath || item.s3Key);
    try {
      await navigator.clipboard.writeText(url);
      showSuccess('Link copied');
    } catch (e) {
      showError('Could not copy to clipboard');
    }
  }, [browser, showError, showSuccess]);

  // -- copy operational link (OPUS-1368) -------------------------------------
  // Same as the public link, but stripped down to the path *inside* assets/
  // (e.g. "PLP\Images\BlahBlah.webp") for pasting into scene/renderer configs.
  const copyOperationalLink = useCallback(async (item) => {
    const relative = logicalToRelative(item.logicalPath || '')
      .split('/')
      .filter(Boolean)
      .join('\\');
    try {
      await navigator.clipboard.writeText(relative);
      showSuccess('Operational link copied');
    } catch (e) {
      showError('Could not copy to clipboard');
    }
  }, [showError, showSuccess]);

  // -- duplicate (single file) ----------------------------------------------
  const duplicateItem = useCallback(async (item) => {
    try {
      const parts = item.logicalPath.split('/');
      const last = parts.pop();
      const ext = last.includes('.') ? last.slice(last.lastIndexOf('.')) : '';
      const base = ext ? last.slice(0, -ext.length) : last;
      const newLogical = [...parts, `${base} (copy)${ext}`].join('/');
      const srcKey = browser.buildS3KeyFor(item.logicalPath);
      const dstKey = browser.buildS3KeyFor(newLogical);
      await S3ApiService.copyObject({
        sourceKey: item.type === 'folder' ? `${srcKey}/` : srcKey,
        destinationKey: item.type === 'folder' ? `${dstKey}/` : dstKey,
        isFolder: item.type === 'folder',
        organizationName, sceneName,
      });
      browser.refresh();
      showSuccess('Duplicated');
    } catch (e) {
      showError(e.message || 'Failed to duplicate');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browser, organizationName, sceneName]);

  // -- upload pipeline ------------------------------------------------------
  const runUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUpload({ active: true, done: 0, total: files.length });
    try {
      const { uploaded, errors } = await browser.uploadFiles(files, {
        onProgress: (done, total) => setUpload({ active: true, done, total }),
      });
      if (errors.length) {
        showError(`${uploaded} uploaded, ${errors.length} failed`);
      } else {
        showSuccess(`Uploaded ${uploaded} file${uploaded === 1 ? '' : 's'}`);
      }
    } catch (e) {
      showError(e.message || 'Upload failed');
    } finally {
      setUpload({ active: false, done: 0, total: 0 });
    }
  }, [browser, showError, showSuccess]);

  // -- drag external (OS) ---------------------------------------------------
  const dragCounter = useRef(0);
  const onContainerDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDraggingExternal(true);
  };
  const onContainerDragLeave = () => {
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDraggingExternal(false);
  };
  const onContainerDragOver = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  const onContainerDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDraggingExternal(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) runUpload(files);
  };

  // -- drag internal (item → folder) ---------------------------------------
  const draggingRef = useRef([]);
  const onItemDragStart = (e, item) => {
    // include all currently-selected items if the dragged one is part of selection
    const items = selection.isSelected(item.logicalPath)
      ? selection.selectedItems
      : [item];
    draggingRef.current = items;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/asset-paths', items.map((i) => i.logicalPath).join('\n'));
  };
  const onItemDragEnd = () => { draggingRef.current = []; setDropTargetPath(null); };
  const onItemDragOver = (folderItem) => setDropTargetPath(folderItem.logicalPath);
  const onItemDragLeave = (folderItem) => setDropTargetPath((c) => (c === folderItem.logicalPath ? null : c));
  const onItemDrop = async (folderItem) => {
    const items = draggingRef.current;
    draggingRef.current = [];
    setDropTargetPath(null);
    if (!items.length) return;
    if (items.some((it) => it.logicalPath === folderItem.logicalPath)) return;
    try {
      await browser.moveItems(items, folderItem.logicalPath);
      showSuccess(`Moved into ${folderItem.name}`);
    } catch (e) { showError(e.message || 'Move failed'); }
  };

  const onCrumbDrop = async (logicalFolderPath) => {
    const items = draggingRef.current;
    draggingRef.current = [];
    setCrumbDropTargetPath(null);
    if (!items.length) return;
    try {
      await browser.moveItems(items, logicalFolderPath);
      showSuccess('Moved');
    } catch (e) { showError(e.message || 'Move failed'); }
  };

  // -- keyboard shortcuts ---------------------------------------------------
  const containerRef = useRef(null);
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const onKey = (e) => {
      if (isTypingTarget(e.target)) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault(); selection.selectAll(); return;
      }
      if (e.key === 'Escape') { selection.clearSelection(); return; }
      if (e.key === 'F2' && selection.selectedItems.length === 1) {
        setInlineRenameOf(selection.selectedItems[0].logicalPath); return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selection.selectedItems.length) setDeleteTargets(selection.selectedItems);
        return;
      }
      if (e.key === 'Enter' && selection.selectedItems.length === 1) {
        onItemDoubleClick(selection.selectedItems[0]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selection, onItemDoubleClick]);

  // -- paste from clipboard (Cmd-V image) ----------------------------------
  useEffect(() => {
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const files = items
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => {
          const file = it.getAsFile();
          if (!file) return null;
          // Give pasted clipboard a friendlier name with extension
          if (file.name && file.name !== 'image.png') return file;
          const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          const name = `pasted-${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}.${ext}`;
          return new File([file], sanitizeFileName(name), { type: file.type });
        })
        .filter(Boolean);
      if (files.length) {
        e.preventDefault();
        runUpload(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [runUpload]);

  const emptyHint = isDraggingExternal
    ? 'Release to upload here'
    : 'Drop files anywhere to upload • Paste images with ⌘V';

  // -------------------------------------------------------------------------
  return (
    <Box
      ref={containerRef}
      onDragEnter={onContainerDragEnter}
      onDragLeave={onContainerDragLeave}
      onDragOver={onContainerDragOver}
      onDrop={onContainerDrop}
      sx={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', height: '100%',
        bgcolor: darkMode ? alpha('#0e0f12', 0.5) : alpha('#fafafa', 0.6),
        borderRadius: 1,
        outline: isDraggingExternal ? `2px dashed ${theme.palette.primary.main}` : 'none',
        outlineOffset: -8,
      }}
    >
      {!hideBreadcrumbs && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Breadcrumbs
            breadcrumbs={browser.breadcrumbs}
            onNavigate={browser.navigateTo}
            onDropOnCrumb={onCrumbDrop}
            dropTargetPath={crumbDropTargetPath}
            setDropTargetPath={setCrumbDropTargetPath}
          />
        </Box>
      )}

      <Box sx={{ px: 2 }}>
        <Toolbar
          searchQuery={searchQuery} onSearchChange={setSearchQuery}
          viewMode={viewMode} onViewModeChange={setViewMode}
          sort={sort} onSortChange={setSort}
          onRefresh={browser.refresh}
          folderStats={browser.folderStats}
          onNewFolder={() => setNewFolderOpen(true)}
          onUploadFiles={runUpload}
          onUploadFolder={runUpload}
          selectedCount={selection.selectedItems.length}
          onClearSelection={selection.clearSelection}
          onBulkDelete={() => setDeleteTargets(selection.selectedItems)}
          onBulkMove={() => setMoveTargets(selection.selectedItems)}
          onBulkDownload={() => downloadAsZip(selection.selectedItems)}
        />
      </Box>

      {(browser.loading || upload.active) && (
        <LinearProgress
          variant={upload.active && upload.total ? 'determinate' : 'indeterminate'}
          value={upload.total ? Math.round((upload.done / upload.total) * 100) : undefined}
          sx={{ mx: 2, mt: 0.5, borderRadius: 1, height: 4 }}
        />
      )}
      {upload.active && upload.total > 0 && (
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
          {upload.done} / {upload.total} files…
        </Typography>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {browser.error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => {}}>
            {browser.error}
          </Alert>
        )}
        {viewMode === 'grid' ? (
          <GridView
            folders={visible.folders} files={visible.files} imageUrls={imageUrls}
            selectedPaths={selection.selectedPaths}
            dropTargetPath={dropTargetPath}
            inlineRenameOf={inlineRenameOf}
            onItemClick={onItemClick}
            onItemDoubleClick={onItemDoubleClick}
            onItemContextMenu={openContextMenu}
            onItemMore={openContextMenu}
            onToggleSelect={(item) => selection.toggleSelection(item.logicalPath)}
            onDragStart={onItemDragStart} onDragEnd={onItemDragEnd}
            onDragOver={onItemDragOver} onDragLeave={onItemDragLeave} onDrop={onItemDrop}
            onInlineRenameCommit={async (item, name) => {
              setInlineRenameOf(null);
              const clean = (name || '').trim();
              if (!clean || clean === item.name) return;
              try { await browser.renameItem(item, clean); showSuccess('Renamed'); }
              catch (e) { showError(e.message || 'Rename failed'); }
            }}
            onInlineRenameCancel={() => setInlineRenameOf(null)}
            darkMode={darkMode}
            emptyHint={emptyHint}
          />
        ) : (
          <ListView
            folders={visible.folders} files={visible.files}
            selectedPaths={selection.selectedPaths}
            dropTargetPath={dropTargetPath}
            inlineRenameOf={inlineRenameOf}
            onItemClick={onItemClick}
            onItemDoubleClick={onItemDoubleClick}
            onItemContextMenu={openContextMenu}
            onItemMore={openContextMenu}
            onToggleSelect={(item) => selection.toggleSelection(item.logicalPath)}
            onDragStart={onItemDragStart} onDragEnd={onItemDragEnd}
            onDragOver={onItemDragOver} onDragLeave={onItemDragLeave} onDrop={onItemDrop}
            onInlineRenameCommit={async (item, name) => {
              setInlineRenameOf(null);
              const clean = (name || '').trim();
              if (!clean || clean === item.name) return;
              try { await browser.renameItem(item, clean); showSuccess('Renamed'); }
              catch (e) { showError(e.message || 'Rename failed'); }
            }}
            onInlineRenameCancel={() => setInlineRenameOf(null)}
            darkMode={darkMode}
            emptyHint={emptyHint}
          />
        )}
      </Box>

      {/* Dialogs ----------------------------------------------------------- */}
      <NewFolderDialog
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        currentLogicalPath={browser.currentLogicalPath}
        onCreate={async (name) => { await browser.createFolder(name); showSuccess('Folder created'); }}
      />
      <RenameDialog
        open={Boolean(renameTarget)}
        item={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRename={async (item, newName) => {
          await browser.renameItem(item, newName);
          showSuccess('Renamed');
        }}
      />
      <DeleteDialog
        open={Boolean(deleteTargets && deleteTargets.length)}
        items={deleteTargets || []}
        onClose={() => setDeleteTargets(null)}
        onConfirm={async (items) => {
          await browser.deleteItems(items);
          selection.clearSelection();
          showSuccess(`Deleted ${items.length} item${items.length === 1 ? '' : 's'}`);
        }}
      />
      <MoveDialog
        open={Boolean(moveTargets && moveTargets.length)}
        items={moveTargets || []}
        onClose={() => setMoveTargets(null)}
        organizationName={organizationName} sceneName={sceneName} captionPathPrefix={captionPathPrefix}
        onConfirm={async (items, destLogical) => {
          await browser.moveItems(items, destLogical);
          selection.clearSelection();
          showSuccess('Moved');
        }}
      />

      {/* Context menu ------------------------------------------------------ */}
      <ContextMenu
        open={ctxMenu.open}
        position={ctxMenu.open ? { x: ctxMenu.x, y: ctxMenu.y } : null}
        item={ctxMenu.item}
        selectedCount={selection.selectedItems.length}
        isEditable={ctxMenu.item ? isEditableFile(ctxMenu.item) : false}
        onClose={closeContextMenu}
        onOpen={() => ctxMenu.item?.type === 'folder' && browser.navigateTo(ctxMenu.item.logicalPath)}
        onPreview={() => ctxMenu.item && onItemDoubleClick(ctxMenu.item)}
        onCodeEdit={() => ctxMenu.item && setCodeEditor(ctxMenu.item)}
        onDownload={() => downloadAsZip(
          selection.selectedItems.length > 1 ? selection.selectedItems : [ctxMenu.item]
        )}
        onCopyLink={() => ctxMenu.item && copyLink(ctxMenu.item)}
        onCopyOperationalLink={() => ctxMenu.item && copyOperationalLink(ctxMenu.item)}
        onRename={() => ctxMenu.item && setRenameTarget(ctxMenu.item)}
        onMove={() => setMoveTargets(
          selection.selectedItems.length > 1 ? selection.selectedItems : (ctxMenu.item ? [ctxMenu.item] : [])
        )}
        onCopy={() => ctxMenu.item && duplicateItem(ctxMenu.item)}
        onDelete={() => setDeleteTargets(
          selection.selectedItems.length > 1 ? selection.selectedItems : (ctxMenu.item ? [ctxMenu.item] : [])
        )}
      />

      {/* Lightbox --------------------------------------------------------- */}
      <Lightbox
        open={Boolean(lightboxStart)}
        images={visibleImages}
        startPath={lightboxStart}
        onClose={() => setLightboxStart(null)}
        buildPublicUrl={browser.buildPublicUrl}
      />

      {/* Code editor ------------------------------------------------------- */}
      {codeEditor && (
        <CodeEditor
          open
          // pass the full S3 key as `path` so CodeEditor's server call works the
          // same in scene mode and caption mode without the editor needing to
          // know about captionPathPrefix.
          file={{ ...codeEditor, path: codeEditor.s3Key }}
          onClose={() => { setCodeEditor(null); browser.refresh(); }}
          organizationName={organizationName}
          sceneName={sceneName}
        />
      )}

      {/* External drop overlay -------------------------------------------- */}
      <Backdrop open={isDraggingExternal} sx={{ zIndex: 5, position: 'absolute', bgcolor: alpha(theme.palette.primary.main, 0.15) }}>
        <Paper elevation={0} sx={{
          p: 4, borderRadius: 3, bgcolor: alpha(theme.palette.background.paper, 0.95),
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        }}>
          <CloudUploadIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h6">Drop to upload to {browser.currentLogicalPath}</Typography>
        </Paper>
      </Backdrop>

      <Snackbar
        open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.message}</Alert> : null}
      </Snackbar>
    </Box>
  );
}

function sortFn(mode) {
  return (a, b) => {
    switch (mode) {
      case 'name-desc': return b.name.localeCompare(a.name);
      case 'modified-asc': return new Date(a.lastModified || 0) - new Date(b.lastModified || 0);
      case 'modified-desc': return new Date(b.lastModified || 0) - new Date(a.lastModified || 0);
      case 'size-asc': return (a.size || 0) - (b.size || 0);
      case 'size-desc': return (b.size || 0) - (a.size || 0);
      case 'type': return (a.fileType || 'zzz').localeCompare(b.fileType || 'zzz') || a.name.localeCompare(b.name);
      case 'name-asc':
      default: return a.name.localeCompare(b.name);
    }
  };
}
